import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Car, Loader2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDealerPortal } from "@/hooks/useDealerPortal";
import { toast } from "sonner";

import { useNavigate } from "react-router-dom";
import { MakeSelectionDialog } from "@/admin/car-card/dialogs/MakeSelectionDialog";
import { ModelSelectionDialog } from "@/admin/car-card/dialogs/ModelSelectionDialog";
import { EngineSelectionDialog } from "@/admin/car-card/dialogs/EngineSelectionDialog";
import { getLanguage } from "@/utils/languageUtils";
import { vehicleCardUILabels } from "@/i18n/labels/vehicleCardLabels";
import {
  bodyTypeLabels,
  colorLabels,
  conditionGradeLabels,
  driveTypeLabels,
  transmissionTypeLabels,
  vehicleTypeLabels,
  CREATE_MANUAL_FUEL_KEYS,
  CREATE_MANUAL_BODY_KEYS,
  CREATE_MANUAL_DRIVE_KEYS,
  CREATE_MANUAL_COLOR_KEYS,
} from "@/i18n/labels/vehicleLabels";
import {
  ConditionGrade,
  TransmissionType,
  CONDITION_GRADE_OPTIONS,
} from "@/types/enums";
import { useDealerPortalNativeSheetProps } from "@/lib/native/useDealerPortalNativeSheetProps";
import { commonButtonLabels } from "@/i18n/labels/commonLabels";
import svVehicleCard from "@/i18n/locales/sv/vehicleCard.json";
import enVehicleCard from "@/i18n/locales/en/vehicleCard.json";
import type {
  DealerPortalVehicle,
  DealerPortalVehicleStatus,
} from "@/types/dealerPortal";
import { VEHICLE_STATUSES } from "@/types/dealerPortal";
import { getDealerPortalVehicleStatusLabels } from "@/i18n";
import { dealerPortalLabels } from "@/i18n";

interface DealerPortalCreateVehicleDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (vehicle: DealerPortalVehicle) => void;
  nested?: boolean;
}

/** Same structure as Skapa manuellt fordonskort (Leadbox). Dealer = company, no owner picker. Submit uses dealer portal API. */
export function DealerPortalCreateVehicleDialog({
  open,
  onClose,
  onSuccess,
  nested = false,
}: DealerPortalCreateVehicleDialogProps) {

  const navigate = useNavigate();
  const { language } = useLanguage();
  const lang = language || getLanguage();
  const ui = vehicleCardUILabels(lang);
  const v = (dealerPortalLabels(lang).vehicles ?? {}) as Record<string, string>;
  const cv = (dealerPortalLabels(lang).createVehicle ?? {}) as Record<
    string,
    string
  >;
  const { createVehicle } = useDealerPortal();
  const nativeSheetProps = useDealerPortalNativeSheetProps(
    open,
    "max-w-2xl max-h-[calc(100dvh-2rem)] sm:max-h-[90vh]",
  );

  const [vehicleDetails, setVehicleDetails] = useState({
    regNumber: "",
    vehicleType: "" as "car" | "mc" | "",
    make: "",
    model: "",
    engine: "",
    year: "",
    mileage: "",
    fuelType: "",
    transmission: "",
    drivetype: "",
    color: "",
    type: "",
    condition: "",
    horsepower: "",
    status: "TEST" as DealerPortalVehicleStatus | "",
    parkingSpot: "",
  });

  const [makeDialogOpen, setMakeDialogOpen] = useState(false);
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [engineDialogOpen, setEngineDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const vehicleCardLocale = lang === "en" ? enVehicleCard : svVehicleCard;
  const fuelLabels =
    (vehicleCardLocale as Record<string, Record<string, string>>).fuelType ??
    {};
  const driveLabels = driveTypeLabels(lang);
  const transmissionLabels = transmissionTypeLabels(lang);
  const bodyLabels = bodyTypeLabels(lang);
  const colorOptsLabels = colorLabels(lang);
  const conditionLabelsMap = conditionGradeLabels(lang);
  const CONDITION_KEYS = CONDITION_GRADE_OPTIONS;

  const handleInputChange = (field: string, value: string) => {
    setVehicleDetails((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === "vehicleType" && value !== prev.vehicleType) {
        updated.make = "";
        updated.model = "";
        updated.engine = "";
      }
      return updated;
    });
    if (errorMessage) setErrorMessage("");
  };

  const handleMakeSelect = (newMake: string) => {
    const makeChanged = newMake !== vehicleDetails.make;
    handleInputChange("make", newMake);
    if (makeChanged) {
      handleInputChange("model", "");
      handleInputChange("engine", "");
    }
  };

  const handleModelSelect = (newModel: string) => {
    const modelChanged = newModel !== vehicleDetails.model;
    handleInputChange("model", newModel);
    if (modelChanged) handleInputChange("engine", "");
  };

  const validateRegNumber = (regNumber: string): boolean => {
    const swedishRegPattern = /^[A-Z]{3}[0-9]{2}[A-Z0-9]$/;
    return swedishRegPattern.test(regNumber);
  };

  const isFormValid = () => {
    const required = [
      "regNumber",
      "vehicleType",
      "make",
      "model",
      "year",
      "mileage",
      "fuelType",
      "color",
      ...(vehicleDetails.vehicleType !== "mc" ? ["drivetype", "type"] : []),
    ];
    const allFilled = required.every(
      (f) =>
        (vehicleDetails[f as keyof typeof vehicleDetails] ?? "")
          .toString()
          .trim() !== "",
    );
    return allFilled && validateRegNumber(vehicleDetails.regNumber);
  };

  const handleSubmit = async () => {
    const required = [
      "regNumber",
      "vehicleType",
      "make",
      "model",
      "year",
      "mileage",
      "fuelType",
      "color",
      ...(vehicleDetails.vehicleType !== "mc" ? ["drivetype", "type"] : []),
    ];
    const missing = required.filter(
      (f) =>
        !(vehicleDetails[f as keyof typeof vehicleDetails] ?? "")
          .toString()
          .trim(),
    );
    if (missing.length > 0) {
      toast.error(ui.missingFields, { description: ui.missingFieldsDesc });
      return;
    }
    if (!validateRegNumber(vehicleDetails.regNumber)) {
      toast.error(ui.invalidRegNumber, { description: ui.invalidRegNumberDesc });
      return;
    }

    setErrorMessage("");
    const regNr = vehicleDetails.regNumber
      .replace(/\s/g, "")
      .replace(/-/g, "")
      .toUpperCase();
    const yearNum = parseInt(vehicleDetails.year, 10);
    const mileageNum = vehicleDetails.mileage.trim()
      ? parseInt(vehicleDetails.mileage.replace(/\s/g, ""), 10)
      : undefined;
    const horsepowerNum = vehicleDetails.horsepower.trim()
      ? parseInt(vehicleDetails.horsepower.replace(/\s/g, ""), 10)
      : undefined;

    createVehicle.mutate(
      {
        regNr,
        make: vehicleDetails.make.trim(),
        model: vehicleDetails.model.trim(),
        year: yearNum,
        status: (vehicleDetails.status || "TEST") as DealerPortalVehicleStatus,
        parkingSpot: vehicleDetails.parkingSpot.trim() || undefined,
        condition: vehicleDetails.condition?.trim() || undefined,
        mileage:
          mileageNum !== undefined && !Number.isNaN(mileageNum)
            ? mileageNum
            : undefined,
        engine: vehicleDetails.engine?.trim() || undefined,
        transmission: vehicleDetails.transmission?.trim() || undefined,
        bodyType: vehicleDetails.type?.trim() || undefined,
        color: vehicleDetails.color?.trim() || undefined,
        fuelType: vehicleDetails.fuelType?.trim() || undefined,
        horsepower:
          horsepowerNum !== undefined && !Number.isNaN(horsepowerNum)
            ? horsepowerNum
            : undefined,
      },
      {
        onSuccess: (data) => {
          toast.success(cv.toastCreated ?? ui.vehicleCardCreated ?? "Fordon tillagt", {
            description: (cv.toastCreatedDesc ?? "").replace(
              "{id}",
              (data as DealerPortalVehicle).id,
            ),
          });
          resetForm();
          onClose();
          onSuccess?.(data as DealerPortalVehicle);
          navigate(
            `/dealer-portal/vehicles/${(data as DealerPortalVehicle).id}`,
          );
        },
        onError: (error: unknown) => {
          const err = error as {
            response?: { status?: number };
            message?: string;
          };
          if (err?.response?.status === 409) {
            setErrorMessage(
              ui.duplicateRegNumber ?? "Registreringsnumret finns redan.",
            );
          } else {
            setErrorMessage(
              (err?.message as string) ??
                cv.toastCreateError ??
                ui.createError ??
                "Kunde inte skapa fordon",
            );
          }
        },
      },
    );
  };

  const resetForm = () => {
    setVehicleDetails({
      regNumber: "",
      vehicleType: "" as "car" | "mc" | "",
      make: "",
      model: "",
      engine: "",
      year: "",
      mileage: "",
      fuelType: "",
      transmission: "",
      drivetype: "",
      color: "",
      type: "",
      condition: "",
      horsepower: "",
      status: "TEST",
      parkingSpot: "",
    });
    setErrorMessage("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
        <DialogContent {...nativeSheetProps} nested={nested}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="h-5 w-5" />
              {cv.title ?? v.addVehicle ?? "Lägg till fordon"}
            </DialogTitle>
          </DialogHeader>

          {cv.viaPostAdHint && (
            <p className="text-sm text-muted-foreground border-l-2 border-primary/50 pl-3 py-1">
              {cv.viaPostAdHint}
            </p>
          )}

          <div className="space-y-6">
            {/* Company – no owner picker; vehicle is company-owned */}
            <div className="space-y-2">
              <Label className="text-base font-medium">
                {v.registeredToCompany ?? "Registrering"}
              </Label>
              <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {cv.companyOwned ?? "Fordonet registreras på ert företag."}
                </p>
              </div>
            </div>

            {/* Vehicle details – same as Skapa manuellt fordonskort */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">{ui.vehicleDetails}</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{ui.registrationNumberFull} *</Label>
                  <Input
                    value={vehicleDetails.regNumber}
                    onChange={(e) =>
                      handleInputChange(
                        "regNumber",
                        e.target.value.toUpperCase(),
                      )
                    }
                    placeholder="ABC123"
                    className={
                      vehicleDetails.regNumber &&
                      !validateRegNumber(vehicleDetails.regNumber)
                        ? "border-red-500"
                        : vehicleDetails.regNumber &&
                            validateRegNumber(vehicleDetails.regNumber)
                          ? "border-green-500"
                          : ""
                    }
                  />
                  {vehicleDetails.regNumber &&
                    !validateRegNumber(vehicleDetails.regNumber) && (
                      <p className="text-sm text-red-500">
                        {ui.regNumberFormat}
                      </p>
                    )}
                </div>

                <div className="space-y-2">
                  <Label>{ui.vehicleTypeLabel} *</Label>
                  <Select
                    value={vehicleDetails.vehicleType}
                    onValueChange={(val) =>
                      handleInputChange("vehicleType", val as "car" | "mc")
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={ui.selectVehicleType} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="car">
                        {vehicleTypeLabels(lang).car}
                      </SelectItem>
                      <SelectItem value="mc">
                        {vehicleTypeLabels(lang).mc}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{ui.make} *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setMakeDialogOpen(true)}
                    className="w-full justify-start"
                  >
                    {vehicleDetails.make || ui.selectMake}
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>{ui.model} *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setModelDialogOpen(true)}
                    disabled={!vehicleDetails.make}
                    className="w-full justify-start"
                  >
                    {vehicleDetails.model || ui.selectModel}
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>{ui.engine} *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEngineDialogOpen(true)}
                    disabled={!vehicleDetails.make}
                    className="w-full justify-start"
                  >
                    {vehicleDetails.engine || ui.selectEngine}
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>{ui.year} *</Label>
                  <Input
                    type="number"
                    value={vehicleDetails.year}
                    onChange={(e) => handleInputChange("year", e.target.value)}
                    placeholder="2020"
                    min={1900}
                    max={2030}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{ui.mileageKm} *</Label>
                  <Input
                    type="number"
                    value={vehicleDetails.mileage}
                    onChange={(e) =>
                      handleInputChange("mileage", e.target.value)
                    }
                    placeholder="50000"
                    min={0}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{ui.fuelType} *</Label>
                  <Select
                    value={vehicleDetails.fuelType}
                    onValueChange={(val) => handleInputChange("fuelType", val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={ui.selectFuelType} />
                    </SelectTrigger>
                    <SelectContent>
                      {CREATE_MANUAL_FUEL_KEYS.map((key) => (
                        <SelectItem key={key} value={key}>
                          {(fuelLabels as Record<string, string>)[key] ?? key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {vehicleDetails.vehicleType !== "mc" && (
                  <div className="space-y-2">
                    <Label>{ui.transmission ?? "Växellåda"}</Label>
                    <Select
                      value={vehicleDetails.transmission}
                      onValueChange={(val) =>
                        handleInputChange("transmission", val)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            ui.selectTransmission ?? "Välj växellåda"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {(
                          Object.entries(transmissionLabels) as [
                            TransmissionType,
                            string,
                          ][]
                        ).map(([techKey, label]) => (
                          <SelectItem key={techKey} value={techKey}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {vehicleDetails.vehicleType !== "mc" && (
                  <div className="space-y-2">
                    <Label>{ui.driveTypeLabel} *</Label>
                    <Select
                      value={vehicleDetails.drivetype}
                      onValueChange={(val) =>
                        handleInputChange("drivetype", val)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={ui.selectDriveType} />
                      </SelectTrigger>
                      <SelectContent>
                        {CREATE_MANUAL_DRIVE_KEYS.map((key) => (
                          <SelectItem key={key} value={key}>
                            {driveLabels[key] ?? key}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>{ui.color} *</Label>
                  <Select
                    value={vehicleDetails.color}
                    onValueChange={(val) => handleInputChange("color", val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={ui.selectColor} />
                    </SelectTrigger>
                    <SelectContent>
                      {CREATE_MANUAL_COLOR_KEYS.map((key) => (
                        <SelectItem key={key} value={key}>
                          {colorOptsLabels[key] ?? key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {vehicleDetails.vehicleType !== "mc" && (
                  <div className="space-y-2">
                    <Label>{ui.bodyType} *</Label>
                    <Select
                      value={vehicleDetails.type}
                      onValueChange={(val) => handleInputChange("type", val)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={ui.selectBodyType} />
                      </SelectTrigger>
                      <SelectContent>
                        {CREATE_MANUAL_BODY_KEYS.map((key) => (
                          <SelectItem key={key} value={key}>
                            {bodyLabels[key] ?? key}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>{ui.condition}</Label>
                  <Select
                    value={vehicleDetails.condition}
                    onValueChange={(val) => handleInputChange("condition", val)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={ui.selectCondition} />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_KEYS.map((key) => (
                        <SelectItem key={key} value={key}>
                          {conditionLabelsMap[key as ConditionGrade] ?? key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{ui.horsepower}</Label>
                  <Input
                    value={vehicleDetails.horsepower}
                    onChange={(e) =>
                      handleInputChange("horsepower", e.target.value)
                    }
                    placeholder={ui.horsepowerPlaceholder}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{v.statusLabel ?? "Status"} *</Label>
                  <Select
                    value={vehicleDetails.status || undefined}
                    onValueChange={(val) => handleInputChange("status", val)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VEHICLE_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {getDealerPortalVehicleStatusLabels(lang)[s]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{v.parkingSpot ?? "P-plats"}</Label>
                  <Input
                    value={vehicleDetails.parkingSpot}
                    onChange={(e) =>
                      handleInputChange("parkingSpot", e.target.value)
                    }
                    placeholder="T.ex. P-plats 12"
                  />
                </div>
              </div>
            </div>

            {errorMessage && (
              <div className="p-3 border border-red-500 rounded-lg bg-red-50 dark:bg-red-900/20">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {errorMessage}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose}>
                {commonButtonLabels(lang).cancel}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={createVehicle.isPending || !isFormValid()}
              >
                {createVehicle.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {commonButtonLabels(lang).creating}
                  </>
                ) : (
                  (v.save ??
                  commonButtonLabels(lang).createVehicleCard ??
                  "Spara")
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <MakeSelectionDialog
        open={makeDialogOpen}
        onOpenChange={setMakeDialogOpen}
        selectedMake={vehicleDetails.make}
        onSelect={handleMakeSelect}
        vehicleType={vehicleDetails.vehicleType || "car"}
      />
      <ModelSelectionDialog
        open={modelDialogOpen}
        onOpenChange={setModelDialogOpen}
        selectedMake={vehicleDetails.make}
        selectedModel={vehicleDetails.model}
        onSelect={handleModelSelect}
        vehicleType={vehicleDetails.vehicleType || "car"}
      />
      <EngineSelectionDialog
        open={engineDialogOpen}
        onOpenChange={setEngineDialogOpen}
        selectedMake={vehicleDetails.make}
        selectedEngine={vehicleDetails.engine}
        onSelect={(eng) => handleInputChange("engine", eng)}
        vehicleType={vehicleDetails.vehicleType || "car"}
      />
    </>
  );
}
